<template>
  <div>
    <div class="p-6 pb-0">
      <div class="flex gap-4 mb-6 items-end">
        <div class="flex-1">
          <label class="block text-gray-700 mb-1">Pagina</label>
          <select v-model="selectedPage" class="p93__input" @change="loadComponents">
            <option value="">-- Seleziona --</option>
            <option v-for="p in pages" :key="p" :value="p">{{ p }}</option>
          </select>
        </div>

        <div class="flex-1">
          <label class="block text-gray-700 mb-1">Target</label>
          <select v-model="targetType" class="p93__input" @change="onTargetTypeChange">
            <option value="company">Azienda</option>
            <option value="role">Ruolo</option>
            <option value="user">Utente</option>
          </select>
        </div>

        <div class="flex-1" v-if="targetType !== 'company'">
          <label class="block text-gray-700 mb-1">{{ targetType === 'role' ? 'Ruolo' : 'Utente' }}</label>
          <select v-model="targetId" class="p93__input" @change="loadComponents">
            <option value="">-- Seleziona --</option>
            <template v-if="targetType === 'role'">
              <option v-for="r in roles" :key="r.id" :value="r.id">{{ r.name }}</option>
            </template>
            <template v-else>
              <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }} ({{ u.email }})</option>
            </template>
          </select>
        </div>
      </div>

      <table v-if="components.length" class="min-w-full bg-white rounded-md shadow-lg">
        <thead>
          <tr>
            <th class="py-2 px-4 border-b text-left">Componente</th>
            <th class="py-2 px-4 border-b text-left">Tipo</th>
            <th class="py-2 px-4 border-b text-left">Descrizione</th>
            <th class="py-2 px-4 border-b text-center">Default</th>
            <th class="py-2 px-4 border-b text-center">Policy</th>
            <th class="py-2 px-4 border-b text-center">Azione</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="comp in components" :key="comp.id">
            <td class="py-2 px-4 border-b text-left">
              <span class="font-medium">{{ comp.label || comp.component_key }}</span>
              <span v-if="comp.label" class="text-xs text-gray-400 ml-1">({{ comp.component_key }})</span>
            </td>
            <td class="py-2 px-4 border-b text-left">
              <span class="text-xs bg-gray-100 px-2 py-1 rounded">{{ comp.component_type || '-' }}</span>
            </td>
            <td class="py-2 px-4 border-b text-left text-sm text-gray-500">{{ comp.description || '-' }}</td>
            <td class="py-2 px-4 border-b text-center">
              <span class="text-xs" :class="policyBadgeClass(comp.default_policy)">{{ policyLabel(comp.default_policy) }}</span>
            </td>
            <td class="py-2 px-4 border-b text-center">
              <select
                :value="comp.override ? comp.override.policy : ''"
                @change="setPolicy(comp, $event.target.value)"
                class="p93__input text-sm py-1"
              >
                <option value="">— Default —</option>
                <option v-for="(label, value) in policyOptions" :key="value" :value="value">{{ label }}</option>
              </select>
            </td>
            <td class="py-2 px-4 border-b text-center">
              <button
                v-if="comp.override"
                @click="removePolicy(comp)"
                class="bg-red-500 text-white px-2 py-1 rounded-md text-sm"
              >✕</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else-if="selectedPage && isTargetSelected" class="text-gray-500 mt-4">
        Nessun componente configurato per questa pagina.
      </div>
    </div>
  </div>
</template>

<script>
import { router } from '@inertiajs/vue3';
import axios from 'axios';

export default {
  props: ['pages', 'roles', 'users', 'selectedPage', 'selectedUserId', 'selectedRoleId', 'selectedTargetType'],
  data() {
    return {
      selectedPage: this.$props.selectedPage || '',
      targetType: this.$props.selectedTargetType || (this.$props.selectedUserId ? 'user' : (this.$props.selectedRoleId ? 'role' : 'company')),
      targetId: this.$props.selectedUserId || this.$props.selectedRoleId || '',
      components: [],
      policyOptions: {
        0: 'Hidden',
        1: 'Read-only',
        2: 'Editable',
        3: 'Required',
      },
    };
  },
  computed: {
    isTargetSelected() {
      return this.targetType === 'company' || !!this.targetId;
    },
  },
  mounted() {
    if (this.selectedPage && this.isTargetSelected) {
      this.loadComponents();
    }
  },
  methods: {
    onTargetTypeChange() {
      this.targetId = '';
      this.components = [];
      if (this.targetType === 'company' && this.selectedPage) {
        this.loadComponents();
      }
    },
    async loadComponents() {
      if (!this.selectedPage || !this.isTargetSelected) {
        this.components = [];
        return;
      }
      const params = { page: this.selectedPage };
      if (this.targetType === 'role') {
        params.role_id = this.targetId;
      } else if (this.targetType === 'user') {
        params.user_id = this.targetId;
      }
      const { data } = await axios.get(route('modules.core.policy-components'), { params });
      this.components = data;
    },
    setPolicy(comp, value) {
      if (value === '') {
        if (comp.override) this.removePolicy(comp);
        return;
      }
      router.post(route('modules.core.policy-store'), {
        ui_component_id: comp.id,
        policy: parseInt(value),
        role_id: this.targetType === 'role' ? this.targetId : null,
        user_id: this.targetType === 'user' ? this.targetId : null,
      }, {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => this.loadComponents(),
      });
    },
    removePolicy(comp) {
      router.delete(route('modules.core.policy-destroy', comp.override.id), {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => this.loadComponents(),
      });
    },
    policyLabel(value) {
      return this.policyOptions[value] ?? '—';
    },
    policyBadgeClass(value) {
      return {
        'bg-red-100 text-red-700 px-2 py-1 rounded': value === 0,
        'bg-yellow-100 text-yellow-700 px-2 py-1 rounded': value === 1,
        'bg-green-100 text-green-700 px-2 py-1 rounded': value === 2,
        'bg-blue-100 text-blue-700 px-2 py-1 rounded': value === 3,
      };
    },
  },
};
</script>
